import { DEFAULT_MIDDLE_DIALOG_WIDTH } from '@abb-procure/constants';
import { Overlay } from '@angular/cdk/overlay';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import type { RenewDialog } from '../components/renew-dialog/renew-dialog.component';
import type { SessionsDialog } from '../components/sessions-dialog/sessions-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class SessionsDialogService {
  private sessionsDialogPromise?: Promise<MatDialogRef<SessionsDialog, void>>;

  private renewDialogPromise?: Promise<MatDialogRef<RenewDialog, void>>;

  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly overlay = inject(Overlay);

  async openSessionsDialog(): Promise<MatDialogRef<SessionsDialog, void>> {
    if (this.sessionsDialogPromise) {
      return this.sessionsDialogPromise;
    }

    this.sessionsDialogPromise = (async (): Promise<
      MatDialogRef<SessionsDialog, void>
    > => {
      const module =
        await import('../components/sessions-dialog/sessions-dialog.component');

      const dialog = this.dialog.open<SessionsDialog, null, void>(
        module.SessionsDialog,
        {
          panelClass: ['sessions-dialog'],
          width: DEFAULT_MIDDLE_DIALOG_WIDTH,
          scrollStrategy: this.overlay.scrollStrategies.block(),
        },
      );

      dialog
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.sessionsDialogPromise = undefined;
        });

      return dialog;
    })();

    return this.sessionsDialogPromise;
  }

  async openRenewDialog(): Promise<MatDialogRef<RenewDialog, void>> {
    if (this.renewDialogPromise) {
      return this.renewDialogPromise;
    }

    this.renewDialogPromise = (async (): Promise<
      MatDialogRef<RenewDialog, void>
    > => {
      const module =
        await import('../components/renew-dialog/renew-dialog.component');

      const dialog = this.dialog.open<RenewDialog, null, void>(
        module.RenewDialog,
        {
          panelClass: ['renew-dialog'],
          scrollStrategy: this.overlay.scrollStrategies.block(),
        },
      );

      dialog
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.renewDialogPromise = undefined;
        });

      return dialog;
    })();

    return this.renewDialogPromise;
  }
}
